import { ReactNode } from 'react'
import { Location } from 'history'
import { OperationVariables } from '@apollo/client'
import { Column } from 'react-table'

import {
  IdToken,
  PopupLoginOptions,
  RedirectLoginResult,
  getIdTokenClaimsOptions,
  RedirectLoginOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  LogoutOptions
} from '@auth0/auth0-spa-js/dist/typings/global'
import { SearchFacetCriteria } from '../generated/scheduler/graphql'
import {
  AdminUserPermission,
  AlertType,
  BoardItemFragment,
  DiagnosticLogType,
  Maybe,
  PlanItem,
  PlanningTerm,
  SchedulerActiveTerm,
  SortOrder,
  StudentProfile,
  StudentTermGpa
} from '../generated/graphql'

export type PlanOption = {
  id: string
  label: string
}

export type PlanItemOption = {
  courseNumber: string
  creditsMin: number
  creditsMax: number
  label: string
  subjectId: string
  title: string
  uniqueKey: string
}

export type CatalogCourse = {
  courseNumber: string
  creditsMin: number
  creditsMax: number
  subjectId: string
  title: string
}

export type Tab = {
  ariaLabel?: string
  content: ReactNode
  href?: string
  onClick?: () => void
  title: string
  /** Show the Dot Badge Adornment on the Tab */
  showBadge?: boolean
}

export interface Auth0User extends Omit<IdToken, '__raw'> {
  appUserId?: number
  email?: string
  firstName?: string | null | undefined
  lastName?: string | null | undefined
  sub: string
  'https://api.student.civitaslearning.com/roles': Roles[]
  'https://api.student.civitaslearning.com/studentId'?: string
  'https://api.student.civitaslearning.com/advisorId'?: string
  'https://api.student.civitaslearning.com/externalId'?: string
  'https://api.student.civitaslearning.com/externalRoles'?: string[]
  permissions?: AdminUserPermission[]
  roles?: Roles[]
}

export enum Roles {
  admin = 'admin',
  advisor = 'advisor',
  student = 'student',
  civitasEmployee = 'civitas-employee'
}

export type Auth0ContextType = {
  getIdTokenClaims(o?: getIdTokenClaimsOptions): Promise<IdToken>
  getTokenSilently(o?: GetTokenSilentlyOptions): Promise<string | undefined>
  getTokenWithPopup(o?: GetTokenWithPopupOptions): Promise<string | undefined>
  handleRedirectCallback(): Promise<RedirectLoginResult>
  isAuthenticated: boolean
  isInitializing: boolean
  isPopupOpen: boolean
  loginWithPopup(o?: PopupLoginOptions): Promise<void>
  loginWithRedirect(o?: RedirectLoginOptions): Promise<void>
  logout(o?: LogoutOptions): void
  user?: Auth0User
}

export interface CustomLogoutOptions {
  /**
   * The URL where Auth0 will redirect your browser to after the logout.
   *
   * **Note**: If the `client_id` parameter is included, the
   * `returnTo` URL that is provided must be listed in the
   * Application's "Allowed Logout URLs" in the Auth0 dashboard.
   * However, if the `client_id` parameter is not included, the
   * `returnTo` URL must be listed in the "Allowed Logout URLs" at
   * the account level in the Auth0 dashboard.
   *
   * [Read more about how redirecting after logout works](https://auth0.com/docs/logout/guides/redirect-users-after-logout)
   */
  returnTo?: string
  /**
   * The `client_id` of your application.
   *
   * If this property is not set, then the `client_id` that was used during initialization of the SDK is sent to the logout endpoint.
   *
   * If this property is set to `null`, then no client ID value is sent to the logout endpoint.
   *
   * [Read more about how redirecting after logout works](https://auth0.com/docs/logout/guides/redirect-users-after-logout)
   */
  client_id?: string
  /**
   * When supported by the upstream identity provider,
   * forces the user to logout of their identity provider
   * and from Auth0.
   * This option cannot be specified along with the `localOnly` option.
   * [Read more about how federated logout works at Auth0](https://auth0.com/docs/logout/guides/logout-idps)
   */
  federated?: boolean
  /**
   * When `true`, this skips the request to the logout endpoint on the authorization server,
   * effectively performing a "local" logout of the application. No redirect should take place,
   * you should update local logged in state.
   * This option cannot be specified along with the `federated` option.
   */
  localOnly?: boolean
}

export interface CustomBaseLoginOptions {
  /**
   * - `'page'`: displays the UI with a full page view
   * - `'popup'`: displays the UI with a popup window
   * - `'touch'`: displays the UI in a way that leverages a touch interface
   * - `'wap'`: displays the UI with a "feature phone" type interface
   */
  display?: 'page' | 'popup' | 'touch' | 'wap'
  /**
   * - `'none'`: do not prompt user for login or consent on reauthentication
   * - `'login'`: prompt user for reauthentication
   * - `'consent'`: prompt user for consent before processing request
   * - `'select_account'`: prompt user to select an account
   */
  prompt?: 'none' | 'login' | 'consent' | 'select_account'
  /**
   * Maximum allowable elasped time (in seconds) since authentication.
   * If the last time the user authenticated is greater than this value,
   * the user must be reauthenticated.
   */
  max_age?: string | number
  /**
   * The space-separated list of language tags, ordered by preference.
   * For example: `'fr-CA fr en'`.
   */
  ui_locales?: string
  /**
   * Previously issued ID Token.
   */
  id_token_hint?: string
  /**
   * Provides a hint to Auth0 as to what flow should be displayed.
   * The default behavior is to show a login page but you can override
   * this by passing 'signup' to show the signup page instead.
   *
   * This only affects the New Universal Login Experience.
   */
  screen_hint?: string
  /**
   * The user's email address or other identifier. When your app knows
   * which user is trying to authenticate, you can provide this parameter
   * to pre-fill the email box or select the right session for sign-in.
   *
   * This currently only affects the classic Lock experience.
   */
  login_hint?: string
  acr_values?: string
  /**
   * The default scope to be used on authentication requests.
   * The defaultScope defined in the Auth0Client is included
   * along with this scope
   */
  scope?: string
  /**
   * The default audience to be used for requesting API access.
   */
  audience?: string
  /**
   * The name of the connection configured for your application.
   * If null, it will redirect to the Auth0 Login Page and show
   * the Login Widget.
   */
  connection?: string
  /**
   * The Id of an organization to log in to.
   *
   * This will specify an `organization` parameter in your user's login request and will add a step to validate
   * the `org_id` claim in your user's ID Token.
   */
  organization?: string
  /**
   * The Id of an invitation to accept. This is available from the user invitation URL that is given when participating in a user invitation flow.
   */
  invitation?: string
  /**
   * If you need to send custom parameters to the Authorization Server,
   * make sure to use the original parameter name.
   */
  [key: string]: any
}

export interface CustomRedirectLoginOptions extends CustomBaseLoginOptions {
  /**
   * The URL where Auth0 will redirect your browser to with
   * the authentication result. It must be whitelisted in
   * the "Allowed Callback URLs" field in your Auth0 Application's
   * settings.
   */
  redirect_uri?: string
  /**
   * Used to store state before doing the redirect
   */
  appState?: any
  /**
   * Used to add to the URL fragment before redirecting
   */
  fragment?: string
  /**
   * Used to select the window.location method used to redirect
   */
  redirectMethod?: 'replace' | 'assign'
}

export interface CustomGetTokenSilentlyOptions {
  /**
   * When `true`, ignores the cache and always sends a
   * request to Auth0.
   */
  ignoreCache?: boolean
  /**
   * There's no actual redirect when getting a token silently,
   * but, according to the spec, a `redirect_uri` param is required.
   * Auth0 uses this parameter to validate that the current `origin`
   * matches the `redirect_uri` `origin` when sending the response.
   * It must be whitelisted in the "Allowed Web Origins" in your
   * Auth0 Application's settings.
   */
  redirect_uri?: string
  /**
   * The scope that was used in the authentication request
   */
  scope?: string
  /**
   * The audience that was used in the authentication request
   */
  audience?: string
  /** A maximum number of seconds to wait before declaring the background /authorize call as failed for timeout
   * Defaults to 60s.
   */
  timeoutInSeconds?: number
  /**
   * If you need to send custom parameters to the Authorization Server,
   * make sure to use the original parameter name.
   */
  [key: string]: any
}

export type Auth0RedirectState = {
  targetUrl?: string
}

export type FacetInputState = {
  [key: string]: boolean | string | Array<string> | null
}

export type FacetDataModel = {
  id: string
  title: string
}

export type FacetData = {
  [key: string]: Array<FacetDataModel>
}

export type SearchQueryState = {
  facets: Array<SearchFacetCriteria>
  freeTextbook?: boolean | undefined
  includeFullCourses: boolean
  query: string
  registrationNumber?: string | undefined
  termCode: string
}

export type SearchHistory =
  | {
      from?: Location
    }
  | undefined

export type LocationState = {
  title: string
  id: string
  search: string
}

/** Router state the apply-template flow hands to the template page */
export type TemplatePageLocationState = {
  /** Job started by the wizard; the page reads its result once */
  correlationId?: string | null
  success?: boolean
  applyTemplateStudentCount?: number
  /** Set when the server refused the template itself (no title, not published) */
  applyTemplateError?: string
  search?: string
}

export type YearOption = {
  label: string
  value: string
}

export type Student = {
  id: number
  firstName: string
  lastName: string
  preferredName?: string
  externalId: string
  major?: string
  sub?: string
}

export type SearchStudent = StudentProfile & {
  previouslySearched?: boolean
}

export type StudentAcademicProgram = {
  description: string
  programId: string
  type: string
}

export type AdminUser = {
  disabled?: boolean
  id: number
  firstName: string
  lastName: string
  email: string
  permissions?: Array<PermissionEnum>
}

export type PermissionState = {
  accessStudents: boolean
  demand: boolean
  diagnostics: boolean
  eo: boolean
  eoCampuses: object[]
  eoManageUsers: boolean
  eoSubjects: object[]
  manageTemplates: boolean
  manageUsers: boolean
  preloader: boolean
  registrationWorksheet: boolean
  sampleStudentLogin: boolean
  studentSendToCart: boolean
  studentSendToCartAndRegister: boolean
  studentUsageRawData: boolean
  studentUsageReports: boolean
  viewAllStudents: boolean
}

export enum PermissionEnum {
  AccessStudents = 'accessStudents',
  Demand = 'demand',
  Diagnostics = 'diagnostics',
  EO = 'eo',
  EoManageUsers = 'eoManageUsers',
  ManageTemplates = 'manageTemplates',
  ManageUsers = 'manageUsers',
  Preloader = 'preloader',
  RegistrationWorksheet = 'registrationWorksheet',
  SampleStudentLogin = 'sampleStudentLogin',
  StudentSendToCart = 'studentSendToCart',
  StudentSendToCartAndRegister = 'studentSendToCartAndRegister',
  StudentUsageRawData = 'studentUsageRawData',
  StudentUsageReports = 'studentUsageReports',
  ViewAllStudents = 'viewAllStudents'
}

export type PermissionDefinition = {
  type: PermissionEnum
  title: string
  description: string
}

export const PermissionNames = {
  [PermissionEnum.AccessStudents]: 'Access Students',
  [PermissionEnum.Demand]: 'Course and Break Demand',
  [PermissionEnum.Diagnostics]: 'Diagnostics',
  [PermissionEnum.EO]: 'Enrollment Optimization - View and Edit',
  [PermissionEnum.EoManageUsers]: 'Enrollment Optimization - Manage User Permissions',
  [PermissionEnum.ManageTemplates]: 'Manage Templates',
  [PermissionEnum.ManageUsers]: 'Create and Edit Users',
  [PermissionEnum.RegistrationWorksheet]: 'Registration Worksheet',
  [PermissionEnum.SampleStudentLogin]: 'Schedule Planner Login',
  [PermissionEnum.Preloader]: 'Schedule Planner Pre-Loader',
  [PermissionEnum.StudentSendToCart]: 'Student Send To Cart',
  [PermissionEnum.StudentSendToCartAndRegister]: 'Student Send To Cart and Register',
  [PermissionEnum.StudentUsageRawData]: 'Student Usage Raw Data',
  [PermissionEnum.StudentUsageReports]: 'Student Usage Reports',
  [PermissionEnum.ViewAllStudents]: 'View All Students'
}

export const ReportsPermissions: Array<PermissionDefinition> = [
  {
    type: PermissionEnum.StudentUsageReports,
    title: PermissionNames[PermissionEnum.StudentUsageReports],
    description: 'View charts of student usage data.'
  },
  {
    type: PermissionEnum.StudentUsageRawData,
    title: PermissionNames[PermissionEnum.StudentUsageRawData],
    description: 'Export data for unique students and logins.  Includes student IDs.'
  },
  {
    type: PermissionEnum.Demand,
    title: PermissionNames[PermissionEnum.Demand],
    description: 'View spreadsheets of course demand as well as break demand data.'
  }
]

export const AcademicPlanningPermissions = [
  {
    type: PermissionEnum.ManageTemplates,
    title: PermissionNames[PermissionEnum.ManageTemplates],
    description: 'Create, edit, publish, and archive templates'
  },
  {
    type: PermissionEnum.AccessStudents,
    title: PermissionNames[PermissionEnum.AccessStudents],
    description: 'View and search for students'
  },
  {
    type: PermissionEnum.ViewAllStudents,
    title: PermissionNames[PermissionEnum.ViewAllStudents],
    description: 'View and search for all students'
  }
]

export const AccessPermissions = [
  {
    type: PermissionEnum.Diagnostics,
    title: PermissionNames[PermissionEnum.Diagnostics],
    description: 'View logs and edit interface settings.'
  },
  {
    type: PermissionEnum.ManageUsers,
    title: PermissionNames[PermissionEnum.ManageUsers],
    description:
      'The most powerful user. Administrator privileges to create/edit other users. Can assign anyone any permission.'
  }
]

export const ToolPermissions = [
  {
    type: PermissionEnum.SampleStudentLogin,
    title: PermissionNames[PermissionEnum.SampleStudentLogin],
    description:
      'Log into student mode as yourself or as any student ID. (Current schedule and send to cart do not work in this mode.)'
  },
  {
    type: PermissionEnum.Preloader,
    title: PermissionNames[PermissionEnum.Preloader],
    description:
      'Pre-load courses and breaks for students as well as indicate one or more courses as required.'
  },
  {
    type: PermissionEnum.RegistrationWorksheet,
    title: PermissionNames[PermissionEnum.RegistrationWorksheet],
    description:
      'Create bulk schedules for a group of students and download a flat file of registration numbers and student IDs.'
  },
  {
    type: PermissionEnum.StudentSendToCart,
    title: PermissionNames[PermissionEnum.StudentSendToCart],
    description: 'Send to Cart on behalf of a student.'
  },
  {
    type: PermissionEnum.StudentSendToCartAndRegister,
    title: PermissionNames[PermissionEnum.StudentSendToCartAndRegister],
    description: 'Send to Cart and Register on behalf of a student.'
  },
  {
    type: PermissionEnum.EO,
    title: PermissionNames[PermissionEnum.EO],
    description: 'Access to the enrollment optimization engine to optimize student registration.'
  },
  {
    type: PermissionEnum.EoManageUsers,
    title: PermissionNames[PermissionEnum.EoManageUsers],
    description:
      'Can control which subjects and campuses Enrollment Optimization users have access to.'
  }
]

export const PermissionValuePairs = [
  { id: PermissionEnum.AccessStudents, value: PermissionNames.accessStudents },
  { id: PermissionEnum.Demand, value: PermissionNames.demand },
  { id: PermissionEnum.ManageUsers, value: PermissionNames.manageUsers },
  { id: PermissionEnum.Diagnostics, value: PermissionNames.diagnostics },
  { id: PermissionEnum.EO, value: PermissionNames.eo },
  { id: PermissionEnum.EoManageUsers, value: PermissionNames.eoManageUsers },
  { id: PermissionEnum.ManageTemplates, value: PermissionNames.manageTemplates },
  { id: PermissionEnum.RegistrationWorksheet, value: PermissionNames.registrationWorksheet },
  { id: PermissionEnum.SampleStudentLogin, value: PermissionNames.sampleStudentLogin },
  { id: PermissionEnum.Preloader, value: PermissionNames.preloader },
  { id: PermissionEnum.StudentSendToCart, value: PermissionNames.studentSendToCart },
  {
    id: PermissionEnum.StudentSendToCartAndRegister,
    value: PermissionNames.studentSendToCartAndRegister
  },
  { id: PermissionEnum.StudentUsageRawData, value: PermissionNames.studentUsageRawData },
  { id: PermissionEnum.StudentUsageReports, value: PermissionNames.studentUsageReports },
  { id: PermissionEnum.ViewAllStudents, value: PermissionNames.viewAllStudents }
]

export type PermissionOption = {
  id: string
  value: string
}

export type DiagnosticState = {
  [key: string]: string | boolean
}

export const LogTypeNames = {
  [DiagnosticLogType.Catalog]: 'Catalog Updates',
  [DiagnosticLogType.EmptyOpenSeat]: 'Include Empty Open Seats',
  [DiagnosticLogType.OpenSeat]: 'Open Seats',
  [DiagnosticLogType.Other]: 'Other',
  [DiagnosticLogType.Schedule]: 'Schedule Updates'
}

export const LogTypeValuePairs = [
  { id: DiagnosticLogType.Schedule, value: LogTypeNames.SCHEDULE },
  { id: DiagnosticLogType.OpenSeat, value: LogTypeNames.OPEN_SEAT },
  { id: DiagnosticLogType.EmptyOpenSeat, value: LogTypeNames.EMPTY_OPEN_SEAT },
  { id: DiagnosticLogType.Catalog, value: LogTypeNames.CATALOG },
  { id: DiagnosticLogType.Other, value: LogTypeNames.OTHER }
]

export type LogTypeOption = {
  id: DiagnosticLogType
  value: string
}

export enum StudentUsageMonth {
  Jan = 'Jan',
  Feb = 'Feb',
  Mar = 'Mar',
  Apr = 'Apr',
  May = 'May',
  Jun = 'Jun',
  Jul = 'Jul',
  Aug = 'Aug',
  Sep = 'Sep',
  Oct = 'Oct',
  Nov = 'Nov',
  Dec = 'Dec'
}

export type StudentUsageDataMonth = {
  month: StudentUsageMonth
  numberOfLogins: number
  numberOfNewStudents: number
}

export type StudentUsageDataYear = {
  months: Array<StudentUsageDataMonth>
  numberOfLogins: number
  numberOfNewStudents: number
  year: string
}

export enum EngagementOpportunities {
  NewComments = 'new-comments',
  UnofferedCourses = 'unoffered-courses',
  Unregistered = 'unregistered',
  NotRegistered = 'not-registered',
  CreditsPlanned = 'credits-planned',
  PlanCreation = 'plan-creation',
  IncomingStudents = 'incoming-students'
}

export type EngagementDetailProps = {
  /** Terms that are active for scheduling */
  activeTerms?: Array<SchedulerActiveTerm>
  /** ExternalId of an advisor, used by civitas-employees to see what that advisor would see */
  advisorExternalId?: string
  /** Whether or not to include all students */
  includeAllStudents: boolean
  /** Callback for term change */
  onTermChange: (term: PlanningTerm & SchedulerActiveTerm) => void
  /** Planning terms */
  planningTerms?: Array<PlanningTerm>
  /** Subset of an engagement opportunity from pie chart */
  qualifier?: string
  /** Term selection from dashboard tile */
  termCode: string
}

export type PaginationState = {
  first: number
  last: number
  before: Maybe<string> | undefined
  after: Maybe<string> | undefined
  startCursor: Maybe<string> | undefined
  endCursor: Maybe<string> | undefined
}

/**
 * Student list narrowed down by Engagement opportunity
 */
export type StudentTableProps = {
  /** Indicates if all students in the table are selected  */
  allSelected: boolean
  /** Default list of columns for all student tables */
  initialColumns: Column<StudentTableInfo>[]
  /** Handle the next page click */
  nextPage: () => void
  /** Handle page size change */
  onPageSizeChange: (size: number) => void
  /** Handle column sort */
  onSort: (key: string, order: SortOrder) => void
  /** Current page index */
  pageIndex: number
  /** Current page size */
  pageSize: number
  /** Pagination details for service side pagination */
  pagination: PaginationState
  /** Handle previous page click */
  previousPage: () => void
  /** Optional qualifier for the table */
  qualifier?: string
  /** Currently selected list of student ids, allSelected = false */
  selectedStudentIds: Array<string>
  /** Handle pagination changes */
  setPagination: (state: any) => void
  /** Current sort info */
  sort: Sort
  /** Currently unselected list of student ids AFTER a select all was selected, allSelected = false */
  unselectedStudentIds: Array<string>
  /** Optional term code for the table */
  termCode?: string
  /** Standard list of variables for each table query */
  variables: OperationVariables
  clearSelection?: () => void
}

export type StudentTableInfo = {
  id: number
  changedAt?: any
  firstName: string
  lastName: string
  lastLogin?: any
  lastLoginFormatted?: string // Formatted display value for lastLogin
  lastViewedAt?: any
  major?: string | null | undefined
  minor?: string | null | undefined
  newPrimaryPlan?: string
  picture?: string
  plannedCredits?: number | null | string | undefined
  planTitle?: string
  previousPrimaryPlan?: string
  studentId: string
  unofferedCoursesCount?: number
  unreadCommentCount?: number
  mostRecent?: any
}

export enum ModalName {
  AcceptableUse = 'ACCEPTABLE_USE',
  AddComment = 'ADD_COMMENT',
  ConfirmApplyTemplateInactiveCourse = 'CONFIRM_APPLY_TEMPLATE_INACTIVE_COURSE',
  ConfirmArchivePlaceholder = 'CONFIRM_ARCHIVE_PLACEHOLDER',
  ConfirmArchiveTemplate = 'CONFIRM_ARCHIVE_TEMPLATE',
  ConfirmClearPlan = 'CONFIRM_CLEAR_PLAN',
  ConfirmCopyPublishedTemplates = 'CONFIRM_COPY_PUBLISHED_TEMPLATES',
  ConfirmDeleteComment = 'CONFIRM_DELETE_COMMENT',
  ConfirmDeleteGroup = 'CONFIRM_DELETE_GROUP',
  ConfirmDeletePlaceholder = 'CONFIRM_DELETE_PLACEHOLDER',
  ConfirmDeletePlan = 'CONFIRM_DELETE_PLAN',
  ConfirmDeleteTemplate = 'CONFIRM_DELETE_TEMPLATE',
  ConfirmLockPublishedTemplates = 'CONFIRM_LOCK_PUBLISHED_TEMPLATES',
  ConfirmReadOnlyBulkAction = 'CONFIRM_READ_ONLY_BULK_ACTION',
  BulkLockProgress = 'BULK_LOCK_PROGRESS',
  ConfirmPublishPlaceholder = 'CONFIRM_PUBLISH_PLACEHOLDER',
  ConfirmPublishTemplate = 'CONFIRM_PUBLISH_TEMPLATE',
  ConfirmReplacePrimary = 'CONFIRM_REPLACE_PRIMARY',
  DownloadStudentUsage = 'DOWNLOAD_STUDENT_USAGE',
  EmailStudent = 'EMAIL_STUDENT',
  MovePlanGroup = 'MOVE_PLAN_GROUP',
  MarkCommentsAsRead = 'MARK_COMMENTS_AS_READ',
  PlanItemDetails = 'PLAN_ITEM_DETAILS',
  ScheduleCompletedCourses = 'SCHEDULE_COMPLETED_COURSES',
  TermsOfService = 'TERMS_OF_SERVICE'
}

export type ModalIdData = {
  id: string
}

export type ModalOwnerIdData = {
  id: string
  ownerAppUserId: number
}

export type ModalPlanItemDetailsData = {
  campus?: string
  disableDelete?: boolean
  disableGroupChange?: boolean
  id: string
  isTemplate: boolean
  planId: string
  readOnly?: boolean
}

export type ModalMarkCommentsAsReadData = {
  advisorExternalId: string
  includeAllStudents: boolean
  selectedStudentsCount: number
  allStudentsCount: number
  selectedStudentIds: Array<string>
  unselectedStudentIds: Array<string>
  onMarkCommentsCompleted(): void
}

export type ModalAddCommentData = {
  groupId: string
  id: string
  isTemplate: boolean
  readOnly: boolean
  showAddComment?: boolean
  showComments?: boolean
  showNewCommentIndicator?: boolean
}

export type ModalApplyTemplateData = {
  id: string
  inactiveCourses: Array<FlatPlanItem>
  search: string
  startTermCode: string
  studentExternalIds: Array<string>
  templateMessage: string
}

export type ModalMoveGroupData = {
  groupId: string
  isTemplate: boolean
  planId: string
}

export type ModalEmailStudentData = {
  toEmailAddress?: string
}

export type ModalDeleteTemplateData = {
  search: string
}

export type ModalDeleteGroupData = {
  isTemplate: boolean
  planId: string
}

export type CompletedCoursesData = {
  completedItems: Array<BoardItemFragment>
  groupTitle: string
  planId: string
  termCode?: string
}

export type ModalScheduleCompletedCoursesData = {
  completedCoursesData: Array<CompletedCoursesData>
}

export type ModalData =
  | ModalAddCommentData
  | ModalApplyTemplateData
  | ModalDeleteGroupData
  | ModalDeleteTemplateData
  | ModalEmailStudentData
  | ModalIdData
  | ModalMoveGroupData
  | ModalOwnerIdData
  | ModalPlanItemDetailsData
  | ModalScheduleCompletedCoursesData
  | ModalMarkCommentsAsReadData

export type ModalPayload = {
  /** Name of Modal */
  name: ModalName
  /** Data to pass to modal */
  data?: ModalData
}

export type ModalContextValue = {
  modalPayload: ModalPayload | null
  showModal: (name: ModalName, data?: ModalData) => void
  hideModal: () => void
}

export enum NotificationType {
  Success = 'SUCCESS',
  Error = 'ERROR',
  Info = 'INFO'
}

export type NotificationPayload = {
  /** Message to show in Notification */
  message: string
  /** Callback event when Close button is clicked */
  onClose?: () => void
  /** Optional function to retry call on error */
  onRetry?: () => void
  /** Show Refresh button on Notification */
  refresh?: boolean
  /** Optional ARIA label to present with the "Retry" Button */
  retryAria?: string
  /** Type of Notification to show */
  type: NotificationType
  /** Optional "View'" URL to show in Notification */
  viewUrl?: string
  /** Optional ARIA label to present with the "View" Link */
  viewAria?: string
}

export type NotificationContextValue = {
  notifPayload: NotificationPayload | null
  showSuccessNotif: (
    message: string,
    viewUrl?: string,
    viewAria?: string,
    onClose?: () => void
  ) => void
  showErrorNotif: (
    message: string,
    onRetry?: () => void,
    retryAria?: string,
    onClose?: () => void
  ) => void
  showInfoNotif: (
    message: string,
    viewUrl?: string,
    viewAria?: string,
    refresh?: boolean,
    onClose?: () => void
  ) => void
  showNotif: (payload: NotificationPayload) => void
  hideNotif: () => void
}

export enum LabelVisibility {
  /** Always visible */
  DEFAULT = 'default',
  /** Always hidden */
  HIDDEN = 'hidden',
  /** Only visible when focused or value is empty */
  VISIBLE_ON_FOCUS = 'visible_on_focus'
}

// Labels for template details drop downs
export type DetailLabelConfig = {
  /** Optional label for the campus field */
  campus?: string
  /** Label for college field */
  college: string
  /** Label for concentration field */
  concentration: string
  /** Label for degree field */
  degree: string
  /** Label for major field */
  major: string
  /** Label for minor field */
  minor: string
}

export type DetailsPlanAlert = {
  /** Unique key of alert */
  alertKey: string
  /** The Alert Type */
  alertType: AlertType
  /** Description of the alert */
  description: string
  /** The associated Plan Item ID */
  itemId: string
  /** The associated Plan ID */
  planId: string
  /** Title of alert */
  title: string
  /** The level of the alert */
  level: string
}

export type MovePlanItemData = {
  /** The new groupId for the item */
  destinationGroupId: string
  /** The unique id of the item */
  itemId: string
  /** The new display sequence / position within the group. */
  newSequence: number
  /** The old display sequence / position within the group. */
  oldSequence: number
  /** The old groupId for the item */
  sourceGroupId: string
}

/**
 * Group of settings animations in the app.
 */
export type AnimationConfig = {
  /**
   * Enable or disable animations.
   * Disabling animations is particularly useful during chromatic builds.
   * */
  enableAnimations: boolean
}

export type CampusOption = {
  /** Optional id for the option */
  id?: string
  /** Code/Key for the dropdown option */
  campusCode: string
  /** Value/text to display for the dropdown option */
  value: string
}

// Option definition for drop downs
export type MajorOption = {
  id?: string
  /** ID/Key for the dropdown option */
  majorCode: string
  /** Value/text to display for the dropdown option */
  value: string
}

// Option definition for drop downs
export type MinorOption = {
  id?: string
  /** Code/Key for the dropdown option */
  minorCode: string
  /** Value/text to display for the dropdown option */
  value: string
}

// Option definition for drop downs
export type CollegeOption = {
  id: string
  /** Code/Key for the dropdown option */
  collegeCode: string
  /** Value/text to display for the dropdown option */
  value: string
}

// Option definition for drop downs
export type DegreeOption = {
  id: string
  /** Code/Key for the dropdown option */
  degreeCode: string
  /** Value/text to display for the dropdown option */
  value: string
}

// Option definition for drop downs
export type ConcentrationOption = {
  id: string
  /** Code/Key for the dropdown option */
  concentrationCode: string
  /** Value/text to display for the dropdown option */
  value: string
}

// Option definition for catalog year dropdown
// Note: both of these values will be the same.  There is no separate code for year.
export type CatalogYearOption = {
  /** Label to display for the year */
  label: string
  /** Value for the year */
  value: string
}

export type ServiceErrorResponse = {
  url: string
  status: number
  statusText: string
  body: {
    timestamp: number
    status: number
    error: string
    message: string
    path: string
  }
}

export interface FlatPlanItem extends PlanItem {
  groupTitle: string
  termCode?: string
}

export interface StudentTermData extends StudentTermGpa {
  term: string
}

export type Sort = {
  key: string
  order: SortOrder
}
